export interface Translation {
  // Navigation
  nav: {
    home: string;
    about: string;
    products: string;
    howItWorks: string;
    compliance: string;
    resources: string;
    faq: string;
    contact: string;
  };

  // Hero Section
  hero: {
    headline: string;
    subheadline: string;
    ctaContact: string;
    ctaCatalog: string;
    ctaDistributor: string;
    notice: string;
    nation: string;
  };

  // About Section
  about: {
    title: string;
    description: string;
  };

  mission: {
    title: string;
    description: string;
  };

  commitment: {
    title: string;
    item1: string;
    item2: string;
    item3: string;
  }

  stats: {
    established: { title: string, description };
    location: { title: string, description };
    industry: { title: string, description };
    // manufacturer: { title: string, description };
    focus: { title: string, description };
  }
  // Products Section
  products: {
    title: string;
    subtitle: string;
    cta: { text: string; buttonText: string; }
    class1: {
      title: string;
      description: string;
      features: string[];
      notice: string;
      downloadIFU: string;
    };
    class4: {
      title: string;
      description: string;
      features: string[];
      notice: string;
      downloadIFU: string;
    };
    class5: {
      title: string;
      description: string;
      features: string[];
      notice: string;
      downloadIFU: string;
    };
    bowieDick: {
      title: string;
      description: string;
      features: string[];
      notice: string;
      downloadIFU: string;
    };
    downloadIFU: string;
    requestQuote: string;
  };

  // How It Works Section
  howItWorks: {
    title: string;
    description: string;
    steps: {
      prepare: { title: string; description: string };
      runCycle: { title: string; description: string };
      checkIndicator: { title: string; description: string };
      recordTrace: { title: string; description: string };
      qualityReview: { title: string; description: string };
    };
  };

  training: {
    title: string;
    description: string;
    button: string;
  }
  // Compliance Section
  compliance: {
    title: string;
    subtitle: string;
    titles: string[];
    points: string[];
    disclaimer: string;
    ctaCertificates: string;
    certification: {
      title: string;
      topics: string[];
      descriptions: string[];
    }
    qualityAssured: {
      title: string;
      description: string;
    }
  };

  // Resources Section
  resources: {
    title: string;
    description: string;
    catalog: string;
    downloadDescriptions: string[];
    downloadButton: string;
    ifu: string;
    // logTemplate: string;
    colorChart: string;
  };

  // FAQ Section
  faq: {
    title: string;
    description: string;
    questions: {
      whatIs: { q: string; a: string };
      whichClass: { q: string; a: string };
      interpret: { q: string; a: string };
      types: { q: string; a: string };
      shelfLife: { q: string; a: string };
      distributor: { q: string; a: string };
    };
  };

  cta: {
    title: string;
    description: string;
    button: string;
  };

  // Contact Section
  contact: {
    title: string;
    subtitle: string;
    address: string;
    ip: string;
    menufacturer: string;
    distributor: string;
    form: {
      name: string;
      organization: string;
      email: string;
      phone: string;
      country: string;
      message: string;
      consent: string;
      submit: string;
      title: string;
      description: string;
    };
    info: {
      address: string;
      ip: string;
      menufacturer: string;
      distributor: string;
      email: string;
      phone: string;
    };
    quickAction: string;
    businessHours: {
      title: string;
      monToFri: string;
      monToFriHour: string;
      sat: string;
      satHour: string;
      description: string;
    }
  };

  // Footer
  footer: {
    copyright: string;
    description: string;
    privacy: string;
    terms: string;
    cookies: string;
    link: string;
    contact: string;
  };

  // Common
  common: {
    loading: string;
    error: string;
    success: string;
    required: string;
  };
}

export const translations: { en: Translation; th: Translation } = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      products: "Products",
      howItWorks: "How It Works",
      compliance: "Compliance",
      resources: "Resources",
      faq: "FAQ",
      contact: "Contact"
    },
    hero: {
      headline: "Chemical Indicators for Reliable Sterilization Monitoring",
      subheadline: "Trusted chemical indicators for steam, EO, and plasma sterilization processes in hospitals, dental clinics, and healthcare facilities.",
      ctaContact: "Contact Sales",
      ctaCatalog: "Download Catalog",
      ctaDistributor: "Become a Distributor",
      notice: "The website is not submitted by the FDA, some products listed on the website during the registration process.",
      nation: "Developer & Manufacturer in Thailand"
    },
    about: {
      title: "About Medical Sterilization Systems Co.,Ltd.",
      description: "Our company was established in May 2023 in Bangkok, Thailand and is engaged in the production and sale of chemical indicators for monitoring sterilization processes in hospitals, dental clinics, and other health care facilities."
    },
    mission: {
      title: "Our Mission",
      description: "To provide reliable and high-quality chemical indicators that enhance the effectiveness of sterilization monitoring, supporting patient safety and infection control in healthcare facilities across Thailand and the region."
    },
    commitment: {
      title: "Our Commitment",
      item1: "Continuous innovation in sterilization monitoring technology",
      item2: "Compliance with international quality standards and best practices",
      item3: "Supporting healthcare professionals with dependable monitoring solutions"
    },
    stats: {
      established: { title: "Established", description: "May 2023" },
      location: { title: "Headquarters", description: "Bangkok, Thailand" },
      industry: { title: "Industry", description: "Medical Devices" },
      // manufacturer: { title: "Manufacturer", description: "Formica Crystal Systems Co.,Ltd." },
      focus: { title: "Key Focus Areas", description: "Quality and Safety" },
    },
    products: {
      title: "Our Products",
      subtitle: "Chemical indicators for various sterilization methods and monitoring requirements",
      cta: { text: "Need help selecting the right indicator for your facility?", buttonText: "Contact Our Experts" },
      class1: {
        title: "Class 1 Process Indicators",
        description: "Steam/Dry air/EO indicators for batch separation",
        features: ["Clear color change indication", "Batch separation capability", "Multiple adhesive options"],
        notice: "this product is not submitted by the FDA",
        downloadIFU: "#"
      },
      class4: {
        title: "Class 4 Multivariable Chemical Indicators",
        description: "Steam indicators responding to time & temperature",
        features: ["Responds to time & temperature", "Available in pouch/strip formats", "Easy interpretation"],
        notice: "",
        downloadIFU: "https://drive.google.com/file/d/1e-9GD6aMozb7DimtKbNYcJrMIE2uepGq/view"
      },
      class5: {
        title: "Class 5 Integrator",
        description: "Integrates multiple critical sterilization variables",
        features: ["Integrates multiple critical variables", "Easy-to-read endpoint", "Reliable performance"],
        notice: "",
        downloadIFU: "https://drive.google.com/file/d/1F0a4v4wS4gQnHyXJiJGDivDrbkzfIeU9/view"
      },
      bowieDick: {
        title: "Bowie-Dick Test Packs",
        description: "Daily vacuum/air removal check for pre-vacuum sterilizers",
        features: ["Daily vacuum check capability", "Air removal verification", "Pre-vacuum sterilizer testing"],
        notice: "this product is not submitted by the FDA",
        downloadIFU: "#"
      },
      downloadIFU: "Download IFU (PDF)",
      requestQuote: "Request Quote"
    },
    howItWorks: {
      title: "How It Works",
      description: "Follow these essential steps to ensure effective sterilization monitoring with our chemical indicators",
      steps: {
        prepare: {
          title: "Prepare & Load",
          description: "Package instruments and place correct indicator type according to protocol"
        },
        runCycle: {
          title: "Run Cycle",
          description: "Execute steam/EO/plasma sterilization per established protocol (time/temp/pressure)"
        },
        checkIndicator: {
          title: "Check Indicator",
          description: "Verify color change and endpoint visibility after cycle completion"
        },
        recordTrace: {
          title: "Record & Trace",
          description: "Log results and retain strips for audit and traceability purposes"
        },
        qualityReview: {
          title: "Quality Review",
          description: "Investigate any failures and reprocess as needed following protocols"
        }
      }
    },
    training: {
      title: "Need Training or Support?",
      description: "Our team provides comprehensive training and ongoing support to ensure proper use of chemical indicators in your facility.",
      button: "Get Support"
    },
    compliance: {
      title: "Compliance & Quality",
      subtitle: "Manufactured under documented quality procedures",
      titles: [
        "Quality Procedures",
        "Performance Standards",
        "Traceability",
        "Documentation"
      ],
      points: [
        "Manufactured under documented quality procedures",
        "Conforms to relevant performance criteria for chemical indicators",
        "Lot/expiry traceability maintained",
        "Instructions for Use (IFU) available for download"
      ],
      disclaimer: "All regulatory references are placeholders. Replace with your actual certifications and registration details before publishing.",
      ctaCertificates: "Request Certificates",
      certification: {
        title: "Standards & Certifications",
        topics: ["ISO 11140-1:2014", " ISO 13485:2016", "Quality Management", "Batch Control"],
        descriptions: ["Sterilization of health care products - Chemical indicators", "Quality management systems in the design and manufacture of medical devices.", "Documented quality procedures and controls", "Complete lot traceability and expiry management"]
      },
      qualityAssured: {
        title: "Quality Assured",
        description: "Every product meets strict quality standards"
      }
    },
    resources: {
      title: "Resources & Downloads",
      description: "Download essential resources to support your sterilization monitoring processes",
      downloadDescriptions: [
        "Complete product catalog with specifications and ordering information",
        "Detailed instructions for use for each product type",
        "Visual reference chart for interpreting color changes"
      ],
      downloadButton: 'Download',
      catalog: "Product Catalog (PDF)",
      ifu: "Instructions for Use (IFU) per product (PDF)",
      // logTemplate: "Sterilization Log Template (XLSX)",
      colorChart: "Color-Change Reference Chart (PNG/PDF)"
    },
    faq: {
      title: "Frequently Asked Questions",
      description: "Find answers to common questions about our chemical indicators and sterilization monitoring",
      questions: {
        whatIs: {
          q: "What is a chemical indicator?",
          a: "A chemical indicator is a device that responds to one or more sterilization parameters with a chemical or physical change that is visible to the naked eye. They help monitor that sterilization conditions have been met."
        },
        whichClass: {
          q: "Which indicator class should I use?",
          a: "The choice depends on your monitoring needs: Class 1 for process indication, Class 4 for multivariable monitoring, Class 5 for integration testing, and Bowie-Dick tests for vacuum verification."
        },
        interpret: {
          q: "How do I interpret color change?",
          a: "Each indicator has specific color change criteria detailed in the Instructions for Use (IFU). Generally, a complete color change indicates proper sterilization conditions were met."
        },
        types: {
          q: "Do you offer indicators for steam/EO/plasma?",
          a: "Yes, we offer chemical indicators suitable for steam, ethylene oxide (EO), and plasma sterilization methods. Each is specifically designed for its respective sterilization process."
        },
        shelfLife: {
          q: "Shelf life and storage conditions?",
          a: "Storage conditions and shelf life vary by product. Please refer to individual product IFU documents for specific storage requirements and expiration information."
        },
        distributor: {
          q: "Distributor/wholesale terms?",
          a: "We offer distributor partnerships and wholesale pricing. Please contact our sales team for detailed terms, minimum order quantities, and partnership opportunities."
        }
      }
    },
    cta: {
      title: "Can't find what you're looking for?",
      description: "Our technical experts are ready to answer your specific questions about chemical indicators and sterilization monitoring.",
      button: "Ask Our Experts"
    },
    contact: {
      title: "Contact Us",
      subtitle: "Get in touch with our team for product information, quotes, or partnership opportunities",
      address: "Address",
      ip: "Developer and intellectual property holder",
      menufacturer: "Contract manufacturer for MSS Co.,Ltd.",
      distributor: "Distributor in Thailand",
      form: {
        name: "Full Name",
        organization: "Organization",
        email: "Email Address",
        phone: "Phone Number",
        country: "Country",
        message: "Message",
        consent: "I consent to being contacted regarding my inquiry",
        submit: "Send Message",
        title: "Send us a Message",
        description: "Fill out the form below and we\'ll get back to you within 24 hours."
      },
      info: {
        address: "Bangkok, Thailand",
        ip: "Medical Sterilization Systems Co.,Ltd. (MSS Co.,Ltd.)",
        menufacturer: "Formica Crystal Systems Co., Ltd.",
        distributor: "Emerald Nonwovens International Co., Ltd.",
        email: "admin@mss.in.th, hassawat.k@mss.in.th",
        phone: "+66 (0)26302115-6 , +66 (0)65986990-0"
      },
      quickAction: "Quick Actions",
      businessHours: {
        title: "Business Hours",
        monToFri: "Monday - Friday",
        monToFriHour: "9:00 AM - 6:00 PM",
        sat: "Saturday",
        satHour: "9:00 AM - 6:00 PM",
        description: "Emergency support available 24/7 for existing customers",
      }
    },
    footer: {
      copyright: "© 2024 Medical Sterilization Systems Co.,Ltd. All rights reserved.",
      description: "Leading manufacturer of chemical indicators for sterilization monitoring in healthcare facilities across Thailand and Southeast Asia.",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      cookies: "Cookie Policy",
      link: "Quick Links",
      contact: "Contact"
    },
    common: {
      loading: "Loading...",
      error: "An error occurred",
      success: "Success!",
      required: "This field is required"
    }
  },
  th: {
    nav: {
      home: "หน้าแรก",
      about: "เกี่ยวกับเรา",
      products: "ผลิตภัณฑ์",
      howItWorks: "วิธีการใช้งาน",
      compliance: "มาตรฐาน",
      resources: "ทรัพยากร",
      faq: "คำถามที่พบบ่อย",
      contact: "ติดต่อเรา"
    },
    hero: {
      headline: "ตัวบ่งชี้ทางเคมีใช้แล้วทิ้งสําหรับตรวจสอบการฆ่าเชื้อของเครืองมือแพทย์ เป็นชือไทยทีใช้จด อย.",
      subheadline: "ตัวบ่งชี้ทางเคมีที่เชื่อถือได้สำหรับไอน้ำ EO และพลาสมาในโรงพยาบาล คลินิกทันตกรรม และสถานพยาบาล",
      ctaContact: "ติดต่อฝ่ายขาย",
      ctaCatalog: "ดาวน์โหลดแคตตาล็อก",
      ctaDistributor: "สมัครเป็นตัวแทนจำหน่าย",
      notice: "เว็บไซต์ยังไม่ได้รับใบอนุญาตโฆษณาเครื่องมือแพทย์ (ฆพ.) จากองค์การอาหารและยา (อย.) ผลิตภัณฑ์ที่แสดงอยู่บนเว็บไซต์เป็นเพียงรายการที่ใช้ในระหว่างกระบวนการขึ้นทะเบียน",
      nation: "คิดค้นและพัฒนาในประเทศไทย"
    },
    about: {
      title: "เกี่ยวกับ บริษัท เมดิคอล สเตอริไลเซชั่น ซิสเต็มส์ จำกัด",
      description: "บริษัทก่อตั้งขึ้นในเดือนพฤษภาคม พ.ศ. 2566 ที่กรุงเทพฯ ประเทศไทย ประกอบธุรกิจผลิตและจำหน่ายตัวบ่งชี้ทางเคมีสำหรับการเฝ้าระวังกระบวนการทำให้ปราศจากเชื้อในโรงพยาบาล คลินิกทันตกรรม และสถานพยาบาลประเภทอื่น ๆ"
    },
    mission: {
      title: "พันธกิจของเรา",
      description: "เพื่อจัดหาตัวบ่งชี้ทางเคมีที่เชื่อถือได้และมีคุณภาพสูง ที่ช่วยให้การเฝ้าระวังการทำให้ปราศจากเชื้อมีประสิทธิภาพ สนับสนุนความปลอดภัยของผู้ป่วยและการควบคุมการติดเชื้อในสถานพยาบาลทั่วประเทศไทยและภูมิภาค"
    },
    commitment: {
      title: "ความมุ่งมั่นของเรา",
      item1: "นวัตกรรมอย่างต่อเนื่องในเทคโนโลยีการเฝ้าระวังการทำให้ปราศจากเชื้อ",
      item2: "การปฏิบัติตามมาตรฐานคุณภาพระดับสากลและแนวปฏิบัติที่ดีที่สุด",
      item3: "สนับสนุนบุคลากรทางการแพทย์ด้วยโซลูชันการเฝ้าระวังที่เชื่อถือได้"
    },
    stats: {
      established: { title: "ก่อตั้ง", description: "พ.ค. 2566" },
      location: { title: "ที่ตั้ง", description: "กรุงเทพฯ ประเทศไทย" },
      industry: { title: "อุตสาหกรรม", description: "อุปกรณ์การแพทย์" },
      // manufacturer: { title: "โรงงานผลิต", description: "Formica Crystal Systems Co.,Ltd." },
      focus: { title: "จุดเน้น", description: "คุณภาพและความปลอดภัย" },
    },
    products: {
      title: "ผลิตภัณฑ์ของเรา",
      subtitle: "ตัวบ่งชี้ทางเคมีสำหรับวิธีการทำให้ปราศจากเชื้อและความต้องการในการเฝ้าระวังต่างๆ",
      cta: { text: "ต้องการความช่วยเหลือในการเลือกตัวบ่งชี้ที่เหมาะสมสำหรับสถานพยาบาลของคุณ?", buttonText: "ติดต่อผู้เชี่ยวชาญของเรา" },
      class1: {
        title: "แผ่น/เทป ตัวบ่งชี้ทางเคมี ประเภทที่1",
        description: "สำหรับแยกแยะการผ่านกระบวนการไอน้ำ/EO/พลาสมา",
        features: ["การเปลี่ยนสีที่ชัดเจน", "ความสามารถในการแยกแยะแบทช์", "ตัวเลือกกาวหลากหลาย"],
        notice: "ผลิตภัณฑ์ชนิดนี้ยังไม่ได้รับการรับรองจาก อย.",
        downloadIFU: "#"
      },
      class4: {
        title: "ตัวบ่งชี้ทางเคมีสำหรับการฆ่าเชื้อแบบอบไอน้ำ ประเภทที่4",
        description: "ตัวบ่งชี้ไอน้ำที่ตอบสนองต่อเวลาและอุณหภูมิ",
        features: ["ตอบสนองต่อเวลาและอุณหภูมิ", "มีทั้งรูปแบบซองและแผ่น", "ตีความง่าย"],
        notice: "",
        downloadIFU: "https://drive.google.com/file/d/1e-9GD6aMozb7DimtKbNYcJrMIE2uepGq/view"
      },
      class5: {
        title: "ตัวบ่งชี้ทางเคมีสำหรับการฆ่าเชื้อแบบอบไอน้ำประเภทที่5",
        description: "รวมตัวแปรสำคัญหลายตัวของการทำให้ปราศจากเชื้อ",
        features: ["รวมตัวแปรสำคัญหลายตัว", "จุดสิ้นสุดที่อ่านง่าย", "ประสิทธิภาพที่เชื่อถือได้"],
        notice: "",
        downloadIFU: "https://drive.google.com/file/d/1F0a4v4wS4gQnHyXJiJGDivDrbkzfIeU9/view"
      },
      bowieDick: {
        title: "ชุดทดสอบ Bowie-Dick",
        description: "การตรวจสอบสุญญากาศ/การขจัดอากาศรายวันสำหรับเครื่องทำให้ปราศจากเชื้อแบบสุญญากาศ",
        features: ["ความสามารถในการตรวจสอบสุญญากาศรายวัน", "การยืนยันการขจัดอากาศ", "การทดสอบเครื่องทำให้ปราศจากเชื้อแบบสุญญากาศ"],
        notice: "ผลิตภัณฑ์ชนิดนี้ยังไม่ได้รับการรับรองจาก อย.",
        downloadIFU: "#"
      },
      downloadIFU: "ดาวน์โหลด IFU (PDF)",
      requestQuote: "ขอใบเสนอราคา"
    },
    howItWorks: {
      title: "วิธีการใช้งาน",
      description: "ปฏิบัติตามขั้นตอนสำคัญเหล่านี้เพื่อให้การเฝ้าระวังการทำให้ปราศจากเชื้อมีประสิทธิภาพด้วยตัวบ่งชี้ทางเคมีของเรา",
      steps: {
        prepare: {
          title: "เตรียมและบรรจุ",
          description: "บรรจุเครื่องมือและวางตัวบ่งชี้ประเภทที่ถูกต้องตามโปรโตคอล"
        },
        runCycle: {
          title: "ดำเนินการรอบ",
          description: "ดำเนินการทำให้ปราศจากเชื้อด้วยไอน้ำ/EO/พลาสมาตามโปรโตคอลที่กำหนด (เวลา/อุณหภูมิ/ความดัน)"
        },
        checkIndicator: {
          title: "ตรวจสอบตัวบ่งชี้",
          description: "ตรวจสอบการเปลี่ยนสีและการมองเห็นจุดสิ้นสุดหลังจากรอบเสร็จสิ้น"
        },
        recordTrace: {
          title: "บันทึกและติดตาม",
          description: "บันทึกผลลัพธ์และเก็บแผ่นไว้เพื่อการตรวจสอบและการติดตาม"
        },
        qualityReview: {
          title: "ทบทวนคุณภาพ",
          description: "ตรวจสอบความล้มเหลวใดๆ และดำเนินการใหม่ตามที่จำเป็นตามโปรโตคอล"
        }
      }
    },
    training: {
      title: "ต้องการการอบรมหรือการสนับสนุน?",
      description: "ทีมของเรามีการอบรมที่ครอบคลุมและการสนับสนุนอย่างต่อเนื่องเพื่อให้มั่นใจในการใช้ตัวบ่งชี้ทางเคมีอย่างถูกต้องในสถานที่ใช้งานของคุณ",
      button: "รับการสนับสนุน"
    },
    compliance: {
      title: "การปฏิบัติตามมาตรฐานและคุณภาพ",
      subtitle: "ผลิตภายใต้ขั้นตอนคุณภาพที่มีเอกสาร",
      titles: [
        "ขั้นตอนคุณภาพ",
        "มาตรฐานประสิทธิภาพ",
        "การติดตาม",
        "เอกสาร"
      ],
      points: [
        "ผลิตภายใต้ขั้นตอนคุณภาพที่มีเอกสาร",
        "สอดคล้องกับเกณฑ์ประสิทธิภาพที่เกี่ยวข้องสำหรับตัวบ่งชี้ทางเคมี",
        "การติดตาม lot/วันหมดอายุ",
        "คำแนะนำการใช้งาน (IFU) พร้อมให้ดาวน์โหลด"
      ],
      disclaimer: "ข้อมูลด้านการกำกับดูแลเป็นตัวอย่าง โปรดปรับให้สอดคล้องกับใบรับรองและการขึ้นทะเบียนจริงก่อนเผยแพร่",
      ctaCertificates: "ขอใบรับรอง",
      certification: {
        title: "มาตรฐานและใบรับรอง",
        topics: ["ISO 11140-1:2014", " ISO 13485:2016", "Quality Management", "Batch Control"],
        descriptions: ["การทำให้ปราศจากเชื้อของผลิตภัณฑ์การดูแลสุขภาพ - ตัวบ่งชี้ทางเคมี", "ระบบการจัดการคุณภาพในการออกแบบและการผลิตเครื่องมือแพทย์", "ขั้นตอนและการควบคุมคุณภาพที่มีเอกสาร", "การติดตาม lot และการจัดการวันหมดอายุที่สมบูรณ์"]
      },
      qualityAssured: {
        title: "รับประกันคุณภาพ",
        description: "ทุกผลิตภัณฑ์ผ่านมาตรฐานคุณภาพที่เข้มงวด"
      }
    },
    resources: {
      title: "ทรัพยากรและการดาวน์โหลด",
      description: "ดาวน์โหลดทรัพยากรที่จำเป็นเพื่อสนับสนุนกระบวนการเฝ้าระวังการทำให้ปราศจากเชื้อของคุณ",
      catalog: "แคตตาล็อกผลิตภัณฑ์ (PDF)",
      downloadDescriptions: [
        "แคตตาล็อกผลิตภัณฑ์ที่สมบูรณ์พร้อมข้อมูลจำเพาะและการสั่งซื้อ",
        "คำแนะนำการใช้งานโดยละเอียดสำหรับผลิตภัณฑ์แต่ละประเภท",
        "แผนภูมิอ้างอิงภาพสำหรับการตีความการเปลี่ยนสี"
      ],
      downloadButton: 'ดาวน์โหลด',
      ifu: "คำแนะนำการใช้งาน (IFU) ต่อผลิตภัณฑ์ (PDF)",
      // logTemplate: "แม่แบบบันทึกการทำให้ปราศจากเชื้อ (XLSX)",
      colorChart: "แผนภูมิอ้างอิงการเปลี่ยนสี (PNG/PDF)"
    },
    faq: {
      title: "คำถามที่พบบ่อย",
      description: "ค้นหาคำตอบสำหรับคำถามทั่วไปเกี่ยวกับตัวบ่งชี้ทางเคมีและการเฝ้าระวังการทำให้ปราศจากเชื้อของเรา",
      questions: {
        whatIs: {
          q: "ตัวบ่งชี้ทางเคมีคืออะไร?",
          a: "ตัวบ่งชี้ทางเคมีเป็นอุปกรณ์ที่ตอบสนองต่อพารามิเตอร์การทำให้ปราศจากเชื้อหนึ่งหรือมากกว่าด้วยการเปลี่ยนแปลงทางเคมีหรือทางกายภาพที่มองเห็นได้ด้วยตาเปล่า ช่วยเฝ้าระวังว่าเงื่อนไขการทำให้ปราศจากเชื้อได้รับการปฏิบัติตาม"
        },
        whichClass: {
          q: "ฉันควรใช้ตัวบ่งชี้ชั้นไหน?",
          a: "การเลือกขึ้นอยู่กับความต้องการในการเฝ้าระวังของคุณ: ชั้น 1 สำหรับการบ่งชี้กระบวนการ ชั้น 4 สำหรับการเฝ้าระวังหลายตัวแปร ชั้น 5 สำหรับการทดสอบการรวม และการทดสอบ Bowie-Dick สำหรับการยืนยันสุญญากาศ"
        },
        interpret: {
          q: "ฉันตีความการเปลี่ยนสีอย่างไร?",
          a: "ตัวบ่งชี้แต่ละตัวมีเกณฑ์การเปลี่ยนสีเฉพาะที่ระบุไว้ในคำแนะนำการใช้งาน (IFU) โดยทั่วไป การเปลี่ยนสีที่สมบูรณ์บ่งชี้ว่าเงื่อนไขการทำให้ปราศจากเชื้อที่เหมาะสมได้รับการปฏิบัติตาม"
        },
        types: {
          q: "คุณมีตัวบ่งชี้สำหรับไอน้ำ/EO/พลาสมาหรือไม่?",
          a: "ใช่ เรามีตัวบ่งชี้ทางเคมีที่เหมาะสำหรับการทำให้ปราศจากเชื้อด้วยไอน้ำ เอทิลีนออกไซด์ (EO) และพลาสมา แต่ละตัวได้รับการออกแบบเฉพาะสำหรับกระบวนการทำให้ปราศจากเชื้อของตน"
        },
        shelfLife: {
          q: "อายุการเก็บรักษาและเงื่อนไขการจัดเก็บ?",
          a: "เงื่อนไขการจัดเก็บและอายุการเก็บรักษาแตกต่างกันไปตามผลิตภัณฑ์ โปรดดูเอกสาร IFU ของผลิตภัณฑ์แต่ละรายการสำหรับข้อกำหนดการจัดเก็บเฉพาะและข้อมูลวันหมดอายุ"
        },
        distributor: {
          q: "เงื่อนไขตัวแทนจำหน่าย/ขายส่ง?",
          a: "เรามีความร่วมมือตัวแทนจำหน่ายและราคาขายส่ง โปรดติดต่อทีมขายของเราสำหรับเงื่อนไขโดยละเอียด ปริมาณการสั่งซื้อขั้นต่ำ และโอกาสความร่วมมือ"
        }
      }
    },
    cta: {
      title: "ไม่พบสิ่งที่คุณกำลังมองหา?",
      description: "ผู้เชี่ยวชาญด้านเทคนิคของเราพร้อมตอบคำถามเฉพาะของคุณเกี่ยวกับตัวบ่งชี้ทางเคมีและการเฝ้าระวังการทำให้ปราศจากเชื้อ",
      button: "ถามผู้เชี่ยวชาญของเรา"
    },
    contact: {
      title: "ติดต่อเรา",
      subtitle: "ติดต่อทีมของเราสำหรับข้อมูลผลิตภัณฑ์ ใบเสนอราคา หรือโอกาสความร่วมมือ",
      address: "ที่อยู่",
      ip: "ผู้พัฒนาและผู้ถือทรัพย์สินทางปัญญา",
      menufacturer: "โรงงานผู้ผลิต",
      distributor: "ตัวแทนจำหน่ายในประเทศไทย",
      form: {
        name: "ชื่อ-นามสกุล",
        organization: "องค์กร",
        email: "อีเมล",
        phone: "หมายเลขโทรศัพท์",
        country: "ประเทศ",
        message: "ข้อความ",
        consent: "ฉันยินยอมให้ติดต่อเกี่ยวกับคำถามของฉัน",
        submit: "ส่งข้อความ",
        title: "ส่งข้อความถึงเรา",
        description: "กรอกแบบฟอร์มด้านล่างและเราจะติดต่อกลับภายใน 24 ชั่วโมง"
      },
      info: {
        address: "กรุงเทพฯ ประเทศไทย",
        ip: "เมดิคอล สเตอริไลเซชั่น ซิสเต็มส์ จำกัด",
        menufacturer: "Formica Crystal Systems Co., Ltd",
        distributor: "Emerald Nonwovens International Co., Ltd.",
        email: "admin@mss.in.th, hassawat.k@mss.in.th",
        phone: "+66 (0)26302115-6 +66 (0)65986990-0"
      },
      quickAction: "การดำเนินการด่วน",
      businessHours: {
        title: "เวลาทำการ",
        monToFri: "จันทร์ - ศุกร์",
        monToFriHour: "9:00 - 18:00",
        sat: "เสาร์",
        satHour: "9:00 - 18:00",
        description: "การสนับสนุนฉุกเฉินพร้อมให้บริการ 24/7 สำหรับลูกค้าปัจจุบัน",
      }
    },
    footer: {
      copyright: "© 2024 เมดิคอล สเตอริไลเซชั่น ซิสเต็มส์ จำกัด สงวนลิขสิทธิ์",
      description: "ผู้ผลิตชั้นนำของตัวบ่งชี้ทางเคมีสำหรับการเฝ้าระวังการทำให้ปราศจากเชื้อในสถานพยาบาลทั่วประเทศไทยและเอเชียตะวันออกเฉียงใต้",
      privacy: "นโยบายความเป็นส่วนตัว",
      terms: "เงื่อนไขการให้บริการ",
      cookies: "นโยบายคุกกี้",
      link: "ลิงก์ด่วน",
      contact: "ติดต่อ"
    },
    common: {
      loading: "กำลังโหลด...",
      error: "เกิดข้อผิดพลาด",
      success: "สำเร็จ!",
      required: "จำเป็นต้องกรอกข้อมูลนี้"
    }
  }
};